import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { auth, db } from '../services/firebase';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { Eye, EyeOff } from 'lucide-react';

const Login = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [erro, setErro] = useState('');
  const [loading, setLoading] = useState(false);

  const loginComRetry = async (email, senha) => {
    const MAX_RETRIES = 3;
    const RETRY_DELAY_MS = 1500;
    for (let i = 0; i < MAX_RETRIES; i++) {
      try {
        const userCredential = await signInWithEmailAndPassword(
          auth,
          email,
          senha
        );
        return userCredential;
      } catch (error) {
        if (error.code === 'auth/user-not-found' && i < MAX_RETRIES - 1) {
          await new Promise((res) => setTimeout(res, RETRY_DELAY_MS));
        } else {
          throw error;
        }
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErro('');
    setLoading(true);

    try {
      const userCredential = await loginComRetry(email, senha);
      const { user } = userCredential;

      const userDocRef = doc(db, 'users', user.uid);
      const userDocSnap = await getDoc(userDocRef);

      if (userDocSnap.exists()) {
        const userData = userDocSnap.data();
        const role = userData.role || 'corretor';
        toast.success('Login realizado com sucesso!');
        navigate(role === 'admin' ? '/admin/dashboard' : '/perfil');
      } else {
        setErro(
          'Perfil do usuário não encontrado. Tente novamente em alguns segundos.'
        );
        toast.error('Perfil do usuário não encontrado.');
      }
    } catch (err) {
      console.error('Erro ao fazer login:', err);
      if (err.code === 'auth/user-not-found') {
        setErro('Usuário não encontrado.');
      } else if (err.code === 'auth/wrong-password') {
        setErro('Senha incorreta.');
      } else {
        setErro('Erro ao entrar. Verifique seus dados e tente novamente.');
      }
      toast.error(erro);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center bg-elion-navy text-white">
      <img
        src="/imagens/login-bg.jpg.jpg"
        alt="Profissional no escritório"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-black bg-opacity-70" />

      <div className="relative z-10 max-w-md w-full bg-white/10 backdrop-blur-md rounded-xl shadow-2xl p-8 space-y-6">
        <h1 className="text-3xl font-extrabold text-center mb-4">
          Bem-vindo à comunidade Elion
        </h1>

        {erro && (
          <div className="bg-red-500 text-white p-2 rounded text-center text-sm">
            {erro}
          </div>
        )}

        <form className="space-y-4" onSubmit={handleSubmit}>
          <div>
            <label className="block mb-1 text-sm text-gray-300">E-mail</label>
            <input
              type="email"
              placeholder="seu@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2 rounded bg-gray-800 text-white focus:outline-none focus:ring-2 focus:ring-elion-gold"
            />
          </div>

          <div>
            <label className="block mb-1 text-sm text-gray-300">Senha</label>
            <div className="relative">
              <input
                type={mostrarSenha ? 'text' : 'password'}
                placeholder="••••••••"
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
                className="w-full px-4 py-2 pr-10 rounded bg-gray-800 text-white focus:outline-none focus:ring-2 focus:ring-elion-gold"
              />
              <button
                type="button"
                onClick={() => setMostrarSenha(!mostrarSenha)}
                className="absolute top-1/2 right-3 transform -translate-y-1/2 text-gray-400 hover:text-white"
              >
                {mostrarSenha ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-elion-gold text-elion-navy font-semibold py-2 rounded-full hover:bg-yellow-400 transition shadow-lg hover:scale-105 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading ? 'Entrando...' : 'Entrar'}
          </button>
        </form>

        <div className="flex justify-between text-sm mt-4">
          <Link
            to="/recuperar-senha"
            className="text-gray-300 hover:text-white transition"
          >
            Esqueci minha senha
          </Link>
          <Link
            to="/cadastro-rapido"
            className="text-gray-300 hover:text-white transition"
          >
            Criar nova conta
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Login;
